import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/configureStore";
import SessionVideoStream from "./SessionVideoStream";
import { getMyCamStreamId, getSessionSetting } from "./selectors";
import { updateVolume } from "./sessionSlice";
import useSessionId from "./useSessionId";
import { VenueConnectionData } from "../../app/types";
import LogError from "../logging/LogError";

interface Props {
  stream: OT.Stream;
  vonageSession: OT.Session;
  isTalker: boolean;
  box: {
    height: number;
    left: number;
    top: number;
    width: number;
  };
}

const SessionVideoSubscriberStream = ({
  vonageSession,
  stream,
  isTalker,
  box,
}: Props) => {
  const div = React.createRef<HTMLDivElement>();
  const sessionId = useSessionId();
  const dispatch = useAppDispatch();
  const [subscriber, setSubscriber] = useState<OT.Subscriber>();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const myCamStreamId = useAppSelector(getMyCamStreamId(sessionId));
  const isMuted = useAppSelector(getSessionSetting(sessionId, "isMuted"));

  const isMyStream = useMemo(
    () => myCamStreamId === stream.streamId,
    [myCamStreamId, stream.streamId],
  );

  useEffect(() => {
    if (isMyStream || isMuted) {
      subscriber?.setAudioVolume(0);
    }
    if (!isMyStream && !isMuted) {
      subscriber?.setAudioVolume(100);
    }
  }, [isMuted, isMyStream, subscriber]);

  useEffect(() => {
    subscriber?.on("audioLevelUpdated", ({ audioLevel }) => {
      if (sessionId) {
        dispatch(updateVolume(sessionId, stream.streamId, audioLevel));
      }
    });
    return () => subscriber?.off("audioLevelUpdated");
  }, [dispatch, sessionId, stream.streamId, subscriber]);

  useEffect(() => {
    if (div.current !== undefined && !isSubscribed) {
      // Ensure there are no duplicate video elements
      while (div.current?.firstChild) {
        div.current.removeChild(div.current.firstChild);
      }
      const data = JSON.parse(stream.connection.data) as VenueConnectionData;
      const sub = vonageSession.subscribe(
        stream,
        div.current?.id,
        {
          insertMode: "append",
          showControls: false,
          width: "100%",
          height: "100%",
          fitMode: stream.videoType === "screen" ? "contain" : "cover",
          style: {
            audioBlockedDisplayMode: "off",
            backgroundImageURI: data.avatarUrl,
            nameDisplayMode: "off",
            videoDisabledDisplayMode: "off",
          },
        },
        (error) => {
          if (error) {
            LogError(error);
          }
        },
      );
      setIsSubscribed(true);
      setSubscriber(sub);
    }
  }, [dispatch, sessionId, div, stream, vonageSession, isSubscribed]);

  return (
    <SessionVideoStream
      box={box}
      ref={div}
      isTalker={isTalker}
      stream={stream}
    />
  );
};

export default SessionVideoSubscriberStream;
